import React, { useState, useEffect, useRef } from "react";
import { ChevronDown } from "lucide-react";

export function PageHeader({ eyebrow, title, subtitle, action }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
      <div>
        {eyebrow && (
          <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#F2B50C] font-semibold mb-2">
            {eyebrow}
          </p>
        )}
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-slate-900">
          {title}
        </h1>
        {subtitle && <p className="text-slate-600 mt-2 max-w-2xl">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

const TYPE_STYLES = {
  RFQ: "bg-blue-50 text-blue-800 border-blue-200",
  TENDER: "bg-[#FEF9E7] text-[#D99E00] border-[#FADF8E]",
  RFI: "bg-emerald-50 text-emerald-800 border-emerald-200",
  RFP: "bg-violet-50 text-violet-800 border-violet-200",
};

export function TypeBadge({ type }) {
  return (
    <span className={`font-mono-data text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${TYPE_STYLES[type] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
      {type}
    </span>
  );
}

const STATUS_STYLES = {
  Submitted: "bg-slate-100 text-slate-700 border-slate-200",
  "Under Review": "bg-amber-50 text-amber-800 border-amber-200",
  Shortlisted: "bg-blue-50 text-blue-800 border-blue-200",
  Awarded: "bg-emerald-50 text-emerald-800 border-emerald-200",
  "Not Successful": "bg-red-50 text-red-800 border-red-200",
};

export function StatusBadge({ status }) {
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${STATUS_STYLES[status] || STATUS_STYLES.Submitted}`}>
      {status}
    </span>
  );
}

export function Empty({ icon: Icon, title, subtitle }) {
  return (
    <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-xl bg-white">
      {Icon && <Icon className="h-10 w-10 text-slate-300 mx-auto mb-3" />}
      <p className="font-semibold text-slate-700">{title}</p>
      {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
    </div>
  );
}

const msBtnCls = "px-3 py-2 rounded-md border border-slate-300 bg-white text-sm focus:border-[#00427A] focus:ring-2 focus:ring-[#00427A]/20 outline-none";

export function MultiSelect({ label, options, selected, onToggle, testid, className = "" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const summary = selected.length === 0 ? `All ${label}` : selected.length === 1 ? selected[0] : `${selected.length} selected`;
  return (
    <div className={`relative ${className}`} ref={ref}>
      <button type="button" data-testid={`${testid}-toggle`} onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-2 ${msBtnCls} ${selected.length ? "border-[#00427A] text-[#00427A] font-medium" : "text-slate-600"}`}>
        <span className="truncate">{summary}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div data-testid={`${testid}-menu`} className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg py-1">
          {options.map((o) => {
            const checked = selected.includes(o);
            return (
              <label key={o} data-testid={`${testid}-option-${o}`}
                className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                <input type="checkbox" checked={checked} onChange={() => onToggle(o)}
                  className="h-4 w-4 rounded border-slate-300 text-[#00427A] focus:ring-[#00427A]/20" />
                <span className={checked ? "text-[#00427A] font-medium" : "text-slate-700"}>{o}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
